import ModelEntity from "../../model-entity"
import {EntityType} from "../../../gd/const/item"
import {RequestFormType} from "../../../../app/tools/request-forms2/const/const"
import {ViewType} from "../../../gd/const/views/view-type"
import {ProjectPropertiesData} from "../types";
import ProjectSession from "../project-session";
import {ProjectsTreeSortType} from "../../space/@types";
import {ColumnID} from "../../../gd-modules/manager/columns/types"
import {TagScope} from "../../tag/const";

type MenuSections = {
    info            : boolean
    users           : boolean
    customers       : boolean
    reports         : boolean
    integrations    : boolean
    automations     : boolean
    history         : boolean
    settings        : boolean
}

type SettingsSections = {
    taskSettings        : boolean
    workItems           : boolean
    taskTypes           : boolean
    customFields        : boolean
    views               : boolean
    workflow            : boolean
    taskId              : boolean
    navigation          : boolean

    tasksWorkflowBlock  : boolean

    // Support ticket system only
    emailTemplates      : boolean
}

export type ProjectAvailableMenuOptions = {
    pinStar         : boolean,
    info            : boolean,
    users           : boolean,
    settings        : boolean,
    history         : boolean,
    rename          : boolean,
    editSource      : boolean, // link or embed
    colorIcon       : boolean,
    move            : boolean,
    convert         : boolean,
    duplicate       : boolean,
    saveAsTemplate  : boolean,
    delete          : boolean,

    status          : boolean,
    archive         : boolean,   // instead of status ?

    directLink      : boolean,  // ???
    showInMenu      : boolean   // ???
}

type PropertiesForm = {
    id          : string,
    name        : string,
    type        : RequestFormType,
    publicKey   : string,
}

export default class ProjectProperties extends ModelEntity {

    protected project               : ProjectSession

    public treatAsCatalog           : boolean
    public expenses                 : boolean           // can create expenses
    public forms                    : PropertiesForm[]  // contains ALL forms associated with THIS project
    public url                      : string    // for QuickAddItemType.LINK
    private _defaultSorting?        : ProjectsTreeSortType      // how we sort resources by default - used in workspaces root and sections
    private _defaultColorize?       : boolean      // if we should colorize icons

    // Allowed work items
    public canCreateTask            : boolean = false
    public canCreateProject         : boolean = false
    // public canCreatePage            : boolean = false
    public canCreateMeeting         : boolean = false
    public canCreateLink            : boolean = false
    public canCreateEmbed           : boolean = false
    public canCreateEvent           : boolean = false
    public canCreateDiscussion      : boolean = false
    public canCreateDocument        : boolean = false
    public canCreateGoal            : boolean = false
    public canCreateDashboard       : boolean = false
    public canCreateRequestForms    : boolean = false
    public canCreateAttachments     : boolean = false
    public canArchive               : boolean = false
    public canHaveStatus            : boolean = true   // if true this item may have status ... if not - archive only in future folder should be able to have tatuses !!!!
    public canHaveViews             : boolean = true

    public menuSections             : MenuSections
    public settingsSections         : SettingsSections
    public viewType?                : ViewType
    public tagScope                 : TagScope

    constructor(propertiesConfig:ProjectPropertiesData, project:ProjectSession) {
        super('', EntityType.DYNAMIC_MODEL)

        this.project                = project

        this.treatAsCatalog         = _.get(propertiesConfig,'treatAsCatalog', false)
        this.expenses               = _.get(propertiesConfig,'expenses', false)
        this.forms                  = _.get(propertiesConfig,'forms', [])
        this.url                    = _.get(propertiesConfig,'url', '')
        this.viewType               = _.get(propertiesConfig,'customViewType', undefined)
        this._defaultSorting        = _.get(propertiesConfig,'defaultSorting', undefined)
        this._defaultColorize       = _.get(propertiesConfig,'defaultColorize', undefined)
        this.tagScope               = _.get(propertiesConfig,'tagScope', TagScope.GLOBAL)

        this.menuSections           = {
            info            : true,
            users           : true,
            customers       : true,
            reports         : true,
            integrations    : true,
            automations     : true,
            history         : true,
            settings        : true
        }

        this.settingsSections       = {
            workItems           : true,
            taskTypes           : true,
            customFields        : true,
            views               : true,
            workflow            : true,
            taskId              : true,
            navigation          : true,
            taskSettings        : true,

            tasksWorkflowBlock  : true, // block in workflow section

            // Support ticket system only - off by default, enabled explicitly there
            emailTemplates      : false,
        }

    }

    get canCreate() {
        return _.find([
            this.canCreateTask,
            this.canCreateProject,
            // this.canCreatePage,
            this.canCreateMeeting,
            this.canCreateLink,
            this.canCreateEmbed,
            this.canCreateEvent,
            this.canCreateDiscussion,
            this.canCreateDocument,
            this.canCreateGoal,
            this.canCreateDashboard,
        ], value=>value)
    }

    get onClick(): func | null {
        return null
    }

    get handleExpandInsteadOfOpen():boolean {
        return this.treatAsCatalog
    }

    // More (Right click) menu option
    get availableMenuOptions(): ProjectAvailableMenuOptions {
        return {
            pinStar         : true,
            info            : true,
            users           : true,
            settings        : true,
            history         : true,
            rename          : true,
            editSource      : false,
            colorIcon       : true,
            move            : true,
            convert         : true,
            duplicate       : true,
            saveAsTemplate  : true,

            status          : true,
            archive         : false,

            delete          : true,

            directLink      : true,
            showInMenu      : true,
        }
    }

    static normalize(data:JSONObject) {
        return {
            treatAsCatalog  : _.get(data, 'treatAsCatalog', false),
            expenses        : _.get(data, 'expenses', false),
            forms           : _.get(data, 'forms', []),
            url             : _.get(data, 'url', null),
            customViewType  : _.get(data, 'customViewType', null),
        }
    }

    get hasBulkAction() {
        return true
    }

    // to be used in future ?
    hasField(fieldId:string):boolean {
        return true
    }

    // used in table / gantt
    hasColumn(columnId: ColumnID): boolean {
        return true
    }

    get defaultSorting():ProjectsTreeSortType|undefined {
        return this._defaultSorting
    }

    set defaultSorting(defaultSorting:ProjectsTreeSortType|undefined) {
        // same value - do nothing
        if (this._defaultSorting === defaultSorting) {
            return
        }

        this._defaultSorting = defaultSorting
        this.project.updatedFields.add('properties')
    }

    get defaultColorize():boolean|undefined {
        return this._defaultColorize
    }

    set defaultColorize(newValue:boolean|undefined) {
        // same value - do nothing
        if (this._defaultColorize === newValue) {
            return
        }

        this._defaultColorize = newValue
        this.project.updatedFields.add('properties')
    }


    toJSON():ProjectPropertiesData {
        let res: ProjectPropertiesData = {}

        // omit default values
        if (this.treatAsCatalog)        res.treatAsCatalog = true
        if (this.expenses)              res.expenses = true
        if (this.forms)                 res.forms = this.forms
        if (this.url)                   res.url = this.url
        if (this.viewType)              res.customViewType = this.viewType
        if (this.tagScope)              res.tagScope = this.tagScope

        // weird...
        if (!_.isNil(this.defaultColorize)) {
            res.defaultColorize = this._defaultColorize
        }

        return res
    }
}
